import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormField,
    Input,
    KeyValuePairs,
    Modal,
    SpaceBetween,
} from '@cloudscape-design/components';
import { formatMoney } from '../../util/formatMoney';

export type OrderAccount = 'ISA' | 'CFD';
export type OrderFractionLabel = '1/5' | '1/4' | '1/3' | '1/2' | 'All In' | 'Custom';

export interface OrderConfirmationModalProps {
    /** The brokerage account targeted by this order. Drives the modal header + the leverage warning. */
    account: OrderAccount;
    /** Ticker symbol the order is for. Required so the user can type it back as a confirmation gate. */
    ticker: string;
    /** Latest price we have for the ticker. Falls back to a "Price not available" line when undefined. */
    currentPrice?: number;
    /** Current free cash for the account, used to project post-trade balance. */
    freeCash: number;
    /** GBP value about to be sent to the broker. */
    orderValue: number;
    /** Optional human label for the chosen fraction; shown inline so the user knows what they're confirming. */
    fraction?: OrderFractionLabel;
    /** Async confirm handler. Modal stays open + shows loading until it resolves; consumer closes on success. */
    onConfirm: () => Promise<void>;
    /** Cancel / Esc / dismiss handler. Must NOT call `onConfirm`. */
    onDismiss: () => void;
    /**
     * When true, the user must retype the ticker before the primary button enables.
     * Use for "All In" and any order ≥ 50% of free cash.
     */
    requireTypedConfirm?: boolean;
    /** Loading flag; disables Place order while the network call is in flight. */
    loading?: boolean;
}

/**
 * Confirmation modal that fronts every Trading-212 order placement.
 *
 * The whole point is to make the irrevocable nature of the action explicit:
 * - shows the projected post-trade balance, so the user sees the dent it makes,
 * - reminds them this hits the live broker and cannot be cancelled from here,
 * - for high-stakes orders, gates the primary button behind retyping the ticker.
 *
 * It does NOT change order business logic — `onConfirm` is the existing call.
 */
export const OrderConfirmationModal: React.FC<OrderConfirmationModalProps> = ({
    account,
    ticker,
    currentPrice,
    freeCash,
    orderValue,
    fraction,
    onConfirm,
    onDismiss,
    requireTypedConfirm = false,
    loading = false,
}) => {
    const [typedTicker, setTypedTicker] = useState<string>('');

    // Re-mounting the modal for a different ticker should reset the gate.
    useEffect(() => {
        setTypedTicker('');
    }, [ticker, requireTypedConfirm]);

    const projectedFreeCash = freeCash - orderValue;
    const expectedTicker = ticker.trim().toUpperCase();
    const typedMatchesTicker = typedTicker.trim().toUpperCase() === expectedTicker;
    const primaryDisabled = loading || (requireTypedConfirm && !typedMatchesTicker);

    const handleConfirm = async () => {
        if (primaryDisabled) return;
        await onConfirm();
    };

    return (
        <Modal
            visible={true}
            onDismiss={onDismiss}
            size="medium"
            header={account === 'ISA' ? 'Confirm ISA buy' : 'Confirm CFD buy'}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismiss} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirm}
                            disabled={primaryDisabled}
                            loading={loading}
                        >
                            Place order
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {account === 'CFD' && (
                    <Alert type="warning" header="Leveraged product">
                        Losses can exceed your deposit.
                    </Alert>
                )}

                <Box>
                    This order will be sent to Trading 212 immediately. It cannot be cancelled
                    from this app.
                </Box>

                <KeyValuePairs
                    columns={2}
                    items={[
                        { label: 'Ticker', value: expectedTicker },
                        ...(fraction
                            ? [{ label: 'Fraction', value: fraction }]
                            : []),
                        {
                            label: 'Current price',
                            value:
                                currentPrice !== undefined && currentPrice !== null && !Number.isNaN(currentPrice)
                                    ? currentPrice.toLocaleString()
                                    : 'Price not available',
                        },
                        { label: 'Order value', value: formatMoney(orderValue, 'GBP') },
                        {
                            label: 'Post-trade free cash',
                            value: formatMoney(projectedFreeCash, 'GBP'),
                        },
                        { label: 'Currency', value: 'GBP' },
                    ]}
                />

                {requireTypedConfirm && (
                    <FormField
                        label="Type the ticker to confirm"
                        constraintText={`Expected: ${expectedTicker}`}
                    >
                        <Input
                            value={typedTicker}
                            onChange={({ detail }) => setTypedTicker(detail.value)}
                            placeholder={expectedTicker}
                            disabled={loading}
                            autoFocus
                            ariaLabel="Type the ticker to confirm the order"
                        />
                    </FormField>
                )}
            </SpaceBetween>
        </Modal>
    );
};
